import React, { ReactElement, RefObject, useCallback, useContext, MouseEvent, ChangeEvent, useMemo } from 'react';
import FilterListIcon from '@mui/icons-material/FilterList';
import Box from "@mui/material/Box";
import SearchIcon from '@mui/icons-material/Search';
import Chip from '@mui/material/Chip';
import Toolbar from '@mui/material/Toolbar';
import { useTranslation } from 'react-i18next';
import { IconButton, InputAdornment, TextField, useTheme } from '@mui/material';
import { Clear as ClearIcon, ViewWeekOutlined, MoreVert, Print } from '@mui/icons-material';
import { UIContext } from '../context/UIContext';
import ToolButton from './ToolButton';
import { PrintContext } from '../screens/transactions/context/PrintContext';
import SearchInfoButton, { SearchInfoButtonProps } from './SearchInfoButton';
import SearchInfoContent from '../screens/transactions/components/SearchInfoContent';

export interface TableChip {
  label: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  onDelete?: () => void;
  adminView?: boolean;
  labelType: string;
}

interface EnhancedTableToolbarProps {
  onFilterClick: (event: MouseEvent<HTMLElement>) => void;
  isFilterOpen: boolean;
  chips?: TableChip[];
  filtersButtonRef?: RefObject<HTMLButtonElement>;
  extraButtons?: ReactElement;
  searchValue?: string;
  setSearchValue?: (s: string) => void;
  onColumnClick?: (event: MouseEvent<HTMLElement>) => void;
  isColumnOpen?: boolean;
  tableHasTransactions: boolean;
  onPrintClick?: (event: MouseEvent<HTMLElement>) => void;
  showSearchInfo?: boolean;
  onSearchInfoClick?: () => void;
}

export const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const { onFilterClick, isFilterOpen, chips, filtersButtonRef, extraButtons, searchValue, setSearchValue, onColumnClick, isColumnOpen, tableHasTransactions, onPrintClick, showSearchInfo = false, onSearchInfoClick } = props;
  const { t } = useTranslation();
  const { colorMode, isMobile, isTablet } = useContext(UIContext);
  const theme = useTheme();
  const { isPrintMenuOpen } = useContext(PrintContext);

  const handleChangeSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (setSearchValue) setSearchValue(event.target.value);
  }, [setSearchValue]);

  const searchInfo = useMemo(() => {
    if (!showSearchInfo) return null;
    return (isMobile || isTablet)
      ? <SearchInfoButton onClick={onSearchInfoClick} />
      : <SearchInfoButton
              tooltip={<SearchInfoContent />}
              tooltipProps={{
                  slotProps: {
                      arrow: {
                          sx: {
                              color: theme.palette.info['160p'],
                          },
                      },
                      tooltip: {
                          sx: {
                              backgroundColor: theme.palette.info['160p'],
                              color: theme.palette.info['190p'],
                              padding: '8px',
                              maxWidth: '190px',
                          },
                      },
                  },
              }}
        />
  }, [showSearchInfo, isMobile, isTablet])

  return (
    <>
      <Toolbar
        sx={{
          pl: { sm: 2 },
          pr: { sm: 2 },
          borderBottom: (isMobile || isTablet) ? 0 :
            `1px ${colorMode === 'light' ? theme?.palette?.grey?.['12p'] : theme?.palette?.grey?.['23p']
            } solid`
        }}
      >

        <Box
          sx={{ flex: '1 1 100%', display: 'inline-block', marginTop: '8px', marginBottom: '8px' }}
        >{searchValue !== undefined && setSearchValue !== undefined &&
          <TextField
            placeholder={t('components.searchBar.searchResults')}
            size="small"
            sx={{ marginRight: 'auto' }}
            id="search-input"
            InputProps={{
              inputProps: { 'data-qa-id': "tableSearchInput" },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon htmlColor="icon.default" />
                </InputAdornment>
              ),
              endAdornment:
                searchValue === '' ? (
                  <InputAdornment position="end">
                    {searchInfo}
                  </InputAdornment>
                ) : (
                  <InputAdornment position="end">
                    <IconButton
                      title={t('actions.delete')}
                      onClick={() => { if (setSearchValue) setSearchValue('') }}
                      sx={{ padding: 0 }}
                      data-qa-id="tableSearchInputClearButton"
                    >
                      <ClearIcon htmlColor="icon.default" />
                    </IconButton>
                  </InputAdornment>
                ),
            }}
            value={searchValue}
            onChange={handleChangeSearch}
            fullWidth={(isMobile || isTablet)}
          />}
        </Box>
        {tableHasTransactions && onColumnClick &&
          <Box sx={{ marginRight: { xs: 0, md: '1.5em' }} } >
            <ToolButton
              iconElement={<ViewWeekOutlined />}
              tooltip={t('components.columns')}
              onClick={onColumnClick}
              text={t('components.columns')}
              active={isColumnOpen}
              dataQaId="tableColumnSelectionButton"
            />
          </Box>
        }
        {tableHasTransactions && onPrintClick &&
          <Box sx={{ marginRight: { xs: 0, md: '1.5em' }, gap: '32px' }}>
            <ToolButton
              iconElement={<Print />}
              tooltip={t('screens.dashboarding.print')}
              onClick={(ev) => onPrintClick?.(ev)}
              text={t('screens.dashboarding.print')}
              active={isPrintMenuOpen}
              dataQaId="PrintButton"
            />
          </Box>
        }
        {extraButtons}
        {(isMobile || isTablet) &&
          <Box sx={{ paddingLeft: '0.5rem' }}>
            <ToolButton
              iconElement={<MoreVert />}
              onClick={onFilterClick}
              tooltip={t('components.filters.filterList')}
              active={isFilterOpen}
              buttonRef={filtersButtonRef}
              dataQaId="tableFiltersButtonMobile"
            />
          </Box>}
        {!(isMobile || isTablet) &&
          <Box>
            <ToolButton
              iconElement={<FilterListIcon />}
              tooltip={t('components.filters.filterList')}
              onClick={onFilterClick}
              text={t('components.filters.filters')}
              active={isFilterOpen}
              buttonRef={filtersButtonRef}
              dataQaId="tableFiltersButton"
            />
          </Box>
        }
      </Toolbar>
      {chips &&
        <Toolbar
          variant='dense'
          sx={{
            pl: { sm: 2 },
            pr: { sm: 2 },
          }}
        >
          <div
            style={{ flex: '1 1 100%', display: 'inline-block', marginTop: "12px", marginBottom: "12px" }}
          >
            <div style={{ flex: '1 1 100%', display: 'inline-block' }}>
              {chips.map((chip) => (
                <Chip
                  key={`chip-${chip.labelType}-${chip.label}`}
                  variant="filled"
                  label={chip.label}
                  onClick={chip.onClick}
                  onDelete={chip.onDelete}
                  sx={{ marginRight: '8px' }}
                  data-qa-id={`chip-${chip.labelType}`}
                />
              ))}
            </div>
          </div>
        </Toolbar>
      }
    </>
  );
};
